import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';
import type { ChatMessage, SourceCitation } from '../../types';

interface MessageListProps {
  messages: ChatMessage[];
  onSourceClick?: (source: SourceCitation) => void;
}

export function MessageList({ messages, onSourceClick }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  // Use block: 'nearest' to scroll within the overflow container, not the whole page
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-600 flex items-center justify-center">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1 dark:text-gray-100">
            Welcome to Site Intelligence
          </h3>
          <p className="text-sm text-gray-600 mb-3 dark:text-gray-400">
            Ask questions about your protocols, SOPs, and guidelines.
          </p>
          <div className="text-left bg-gray-50 rounded-lg p-3 dark:bg-gray-800">
            <p className="text-xs font-medium text-gray-700 mb-1 dark:text-gray-300">Try asking:</p>
            <ul className="text-xs text-gray-600 space-y-0.5 dark:text-gray-400">
              <li>"What are the inclusion criteria?"</li>
              <li>"What are the AE reporting requirements?"</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {messages.map((message) => (
        <MessageBubble key={message.id} message={message} onSourceClick={onSourceClick} />
      ))}
      <div ref={messagesEndRef} />
    </div>
  );
}
